import {createApi} from '@reduxjs/toolkit/query/react'

import {Booking} from '../../../api-types/bovio/response/booking'
import {fetchBaseQuery} from '../../../utils/network/fetchBaseQuery'
import {prepareBoVioHeaders} from '../../utils/prepareBovioHeaders'

import type {LastUsedBillingAddress} from '../../../api-types/bovio/response/users/last_used_billing_address'
import type {DeviceCategory} from '../../../utils/userAgent/types'
import type {SharedRequestParams} from '../../types/Request'

export interface GetBookingByIdRequest extends SharedRequestParams {
  httpRequestId: string | undefined
  url: string
  bookingId: string
  locale: string
  isSecure?: boolean
  anonymousId?: string
  deviceType?: DeviceCategory
  /** Cognito owner-session token, attached as x-auth-token when present */
  authToken?: string
  /** Short-lived OTP access token, attached as Authorization: Bearer when present */
  otpToken?: string
}

export interface ExtendedBooking extends Booking {
  httpRequestId: string
}

export interface GetLastUsedBillingAddressRequest extends SharedRequestParams {
  url: string
  authToken: string
}

/**
 * API for retrieving booking-related data, used on checkout (existing bookings) and post-booking pages.
 */
export const bookingRetrieveApi = createApi({
  reducerPath: 'bookingRetrieveApi',
  tagTypes: ['Booking'],
  baseQuery: fetchBaseQuery({
    baseUrl: ''
  }),
  serializeQueryArgs: ({endpointName}) => endpointName,
  endpoints: builder => ({
    getBookingById: builder.query<ExtendedBooking, GetBookingByIdRequest>({
      providesTags: ['Booking'],
      query: ({
        bookingId,
        locale,
        url,
        deviceType,
        anonymousId,
        isSecure = false,
        ...rest
      }) => ({
        url: `${url}${isSecure ? 'secure/' : ''}bookings/${bookingId}`,
        params: {locale, deviceType, anonymousId},
        headers: prepareBoVioHeaders(rest)
      }),
      serializeQueryArgs: ({endpointName, queryArgs}) => {
        return `${endpointName}/${queryArgs.bookingId}`
      },
      transformResponse: (
        response: Booking,
        meta: {headers: Record<string, string>}
      ) => ({...response, httpRequestId: meta.headers?.['x-request-id']})
    }),
    getLastUsedBillingAddress: builder.query<
      LastUsedBillingAddress,
      GetLastUsedBillingAddressRequest
    >({
      query: ({url, ...rest}) => ({
        url,
        method: 'GET',
        headers: prepareBoVioHeaders(rest)
      })
    })
  })
})

/**
 * Exported hooks for interacting with the bookingRetrieveApi
 */
export const {useGetBookingByIdQuery, useGetLastUsedBillingAddressQuery} =
  bookingRetrieveApi
